// 

import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { MessageDialogComponent } from "../message-dialog/message-dialog.component";

@Component({
  selector: "app-keywords-dialog",
  templateUrl: "./keywords-dialog.component.html",
  styleUrls: ["./keywords-dialog.component.scss"],
})
export class KeywordsDialogComponent implements OnInit {
  keywordForm = new FormGroup({
    keywords: new FormControl(""),
  });

  allKeywords: string[] = [];
  selectedKeywords: string[] = [];
  filteredKeywords!: Observable<string[]>;

  // Use reactive form control value for the new keyword input.
  // This avoids potential issues with ngModel in a reactive form context.

  constructor(
    public dialogRef: MatDialogRef<KeywordsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.allKeywords = this.data.allKeywords ?? [];
    this.selectedKeywords = this.data.selectedKeywords ?? [];

    this.filteredKeywords = this.keywordForm.get("keywords")!.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter(value || ""))
    );
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.availableKeywords.filter((keyword) =>
      keyword.toLowerCase().includes(filterValue)
    );
  }

  get availableKeywords(): string[] {
    const selectedLower = new Set(
      this.selectedKeywords.map((k) => k.toLowerCase())
    );
    return this.allKeywords.filter(
      (keyword) => !selectedLower.has(keyword.toLowerCase())
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveKeywords() {
    const confirmDialog = this.dialog.open(MessageDialogComponent, {
      width: "450px",
      autoFocus: false,
      disableClose: true,
      data: {
        message:
          "Updating keywords will modify the compliance documents currently displayed. Changes may take a few moments to apply. Do you want to continue?",
      },
    });
    confirmDialog.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.dialogRef.close({
          selected: this.selectedKeywords,
          available: this.allKeywords,
        });
      }
    });
  }

  addNewKeyword() {
    const control = this.keywordForm.get("keywords");
    const keyword = (control?.value || "").trim();
    if (!keyword) {
      return;
    }

    // Only add to the available list (at the top) if it doesn't already exist
    if (!this.allKeywords.some((item) => item.toLowerCase() === keyword.toLowerCase())) {
      this.allKeywords = [keyword, ...this.allKeywords];
    }

    control?.setValue("");
    this.refreshFilter();
  }

  toggleKeyword(keyword: string): void {
    if (this.isSelected(keyword)) {
      this.remove(keyword);
    } else {
      this.selectedKeywords.push(keyword);
    }
    this.refreshFilter();
  }

  remove(keyword: string): void {
    this.selectedKeywords = this.selectedKeywords.filter(
      (k) => k !== keyword
    );
  }

  isSelected(keyword: string): boolean {
    return this.selectedKeywords.includes(keyword);
  }

  private refreshFilter(): void {
    const currentVal = this.keywordForm.controls.keywords.value;
    this.keywordForm.controls.keywords.setValue(currentVal);
  }
}